import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { CAKey, Scheme, allSchemes } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";
import {
  createCAKey,
  getCAKeys
} from "../utils/helpers/data/certificate-authority";
import Button from "../components/button/Button";
import Modal from "../components/modal/Modal";
import Select, { Option } from "../components/select/Select";
import FileUploader from "../components/file-uploader/FileUploader";
import Input from "../components/input/Input";

const CertificateAuthority: FunctionComponent = () => {
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [dataSource, setDataSource] = useState<CAKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const displayAddEditModal = () => {
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setShowAddEditModal(false);
  };

  const { notify } = useContext(LayoutContext);

  const fetchCAKeys = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);
    const { error, message, data } = await getCAKeys(pagination);
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch certificate authority keys: ${message}`);
      return;
    }
    setDataSource(data.keys);
    setPagination({ ...pagination, total: data.count, pageNumber });
  };

  const columns: Column<CAKey>[] = [
    {
      title: "Key Label",
      dataIndex: "keyLabel",
      key: "keyLabel"
    },
    {
      title: "Scheme",
      dataIndex: "cardScheme",
      key: "cardScheme"
    },
    {
      title: "Activation status",
      dataIndex: "activated",
      key: "activated",
      render: (activated: string) => (
        <span className={`tag smaller ${activated ? "success" : "error"}`}>
          {activated ? "ACTIVE" : "INACTIVE"}
        </span>
      )
    }
  ];

  useEffect(() => {
    fetchCAKeys();
  }, [pagination.pageNumber, pagination.pageSize]);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">
        Certificate Authority Keys
      </h1>
      <div className="flex end vertical-margin spaced">
        <Button onClick={() => displayAddEditModal()}>New CA Key</Button>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onPageChange={pageNumber =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
      />

      <AddEditModal
        visible={showAddEditModal}
        cancel={() => dismissAddEditModal()}
        onComplete={stayOnPage =>
          fetchCAKeys(stayOnPage ? pagination.pageNumber : 1)
        }
      />
    </main>
  );
};

const schemeOptions: Option[] = allSchemes.map(scheme => ({
  label: scheme
    .toLowerCase()
    .replace(/^./, char => char.toUpperCase())
    .replace(/_/, " "),
  value: scheme
}));

interface AddEditProps {
  visible: boolean;
  cancel: () => void;
  onComplete: (stayOnPage: boolean) => void;
}

const AddEditModal: FunctionComponent<AddEditProps> = ({
  visible,
  cancel,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [scheme, setScheme] = useState<Scheme | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [modulusLength, setModulusLength] = useState("");

  const { notify } = useContext(LayoutContext);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error, message } = await createCAKey({
      scheme: scheme as Scheme,
      file: file as File,
      modulusLength
    });
    setLoading(false);
    if (error) {
      notify("error", `Unable to import CA key: ${message}`);
    } else {
      notify("success", `CA key imported successfully`);
      onComplete(false);
      cancel();
    }
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Issuer name</span>
          <Select
            value={scheme || ""}
            options={schemeOptions}
            placeholder="Select Scheme"
            onSelect={value => setScheme(value as Scheme)}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Upload CA key file</span>
          <FileUploader
            acceptedTypes={[]}
            titleText="Upload CA key file"
            subtitle="Or drag CA key file to upload"
            file={file}
            onChange={setFile}
          />
        </div>

        <div className="input-group">
          <span className="question">Modulus Length</span>
          <Input
            value={modulusLength}
            onChange={setModulusLength}
            placeholder="Enter modulus length (in bits)"
            inputValidation={{
              required: true,
              transform: val => val.toUpperCase().replace(/\D/g, "")
            }}
            responsive
          />
        </div>

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            Import new CA key
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CertificateAuthority;
